<script setup lang="ts">
import { inject, nextTick, ref, UnwrapRef, useTemplateRef, watch } from "vue";
import useBackend from "@/composition/useBackend";
import { personalFolderId, Query, QueryColumn, QueryFolder, QueryNavigation, sharedFolderId } from "@/backend/queries";
import {
  legacyModeKey,
  useContext,
  useDialogs,
  useLocalization,
  usePhrases,
  ViewContext,
  ViewOptions
} from "@hantera/portal-app";
import { ContextMenuSection, Tree, TreeNode } from "@hantera/design-system";
import QuickSearch from "@/components/QuickSearch";
import useApps from "@/composition/use-apps";
import log from "@/log";

const context = useContext()
const t = useLocalization();
const { graph, queries } = useBackend();
const dialogs = useDialogs();
const legacyMode = inject(legacyModeKey, false);
const tree = useTemplateRef('tree');
const apps = useApps()
const { authorizeAll, openView } = useContext()
const phrases = usePhrases('navHub')

const nodes = ref<TreeNode[]>([]);

watch(() => [queries.folders, queries.queries], () => {
  updateNodes();
}, {
  immediate: true,
  deep: true
});

function updateNodes() {

  nodes.value = [];

  nodes.value.push(<any>{
    id: 'sales',
    label: 'Sales',
    style: 'header',
    sort: 'none',
    children: [{
      id: 'sales-orders',
      label: 'Orders',
      onClick() {
        context.openView('/sales/orders', {})
      }
    }, {
      id: 'sales-deliveries',
      label: 'Deliveries',
      // tags: [{
      //   value: 10,
      //   style: 'secondary',
      //   title: 'Open'
      // },{
      //   value: 549,
      //   style: 'hue',
      //   title: 'Picking',
      //   hue: 202
      // }],
      onClick() {
        context.openView('/sales/deliveries', {})
      }
    }, {
      id: 'sales-invoices',
      label: 'Invoices',
      onClick() {
        context.openView('/sales/invoices', {})
      }
    }, {
      id: 'sales-skus',
      label: 'Inventory',
      onClick() {
        context.openView('/sales/inventory', {})
      }
    }
      // ,{
      //   id: 'sales-payments',
      //   label: 'Payments',
      //   onClick() {
      //     openView('/sales/payments', {})
      //   }
      // }
    ]
  })

  nodes.value.push(<any>{
    id: 'queries',
    label: 'Queries',
    style: 'header',
    children: queries.folders.map(f => queryFolderToTreeNode(f))
  })

  nodes.value.push(<any>{
    id: 'reports',
    label: 'Reports',
    style: 'header',
    children: []
  })

  // nodes.value.push(<any>{
  //   id: 'marketing',
  //   label: 'Marketing',
  //   style: 'header',
  //   children: [{
  //     id: 'promotion-periods',
  //     label: 'Promotion Periods',
  //     onClick() {
  //       context.openView('/marketing/promotion-periods', {})
  //     }
  //   }]
  // })

  const systemChildren = []

  // Jobs
  if (authorizeAll(['jobs:read', 'job-definitions:read'])) {
    systemChildren.push({
      id: 'settings-jobs',
      label: 'Jobs',
      onClick() {
        context.openView('/jobs/dashboard', {})
      }
    })
  }

  // Any one of the tabs' permissions makes the view worth showing; requiring all of them would
  // hide it from the operators it exists for.
  if (authorizeAll(['traces:read'])
    || authorizeAll(['ingresses:statistics'])
    || authorizeAll(['egresses:statistics'])
    || authorizeAll(['jobs:statistics'])
    || authorizeAll(['ingresses:read'])
    || authorizeAll(['egresses:read'])) {
    systemChildren.push({
      id: 'system-monitoring',
      label: 'Monitoring',
      onClick() {
        context.openView('/system/monitoring', {})
      }
    })
  }


  systemChildren.push({
    id: 'settings-audit',
    label: 'Audit Logs',
    onClick() {
      context.openView('/settings/audit', {})
    }
  })

  // Apps (only if user has read permission)
  if (authorizeAll(['apps:read'])) {
    systemChildren.push({
      id: 'system-apps',
      label: 'Apps',
      onClick() {
        context.openView('/system/apps', {})
      }
    })
  }

  // IAM section (only show if user has any IAM permissions)
  const iamChildren = []

  if (authorizeAll(['iam/principals:read'])) {
    iamChildren.push({
      id: 'iam-principals',
      label: 'Users',
      onClick() {
        context.openView('/iam/principals', {})
      }
    })
  }

  if (authorizeAll(['iam/clients:read'])) {
    iamChildren.push({
      id: 'iam-clients',
      label: 'External Applications',
      onClick() {
        context.openView('/iam/clients', {})
      }
    })
  }

  if (authorizeAll(['iam/roles:read'])) {
    iamChildren.push({
      id: 'iam-roles',
      label: 'Roles',
      onClick() {
        context.openView('/iam/roles', {})
      }
    })
  }

  // Only add IAM section if user has at least one IAM permission
  if (iamChildren.length > 0) {
    systemChildren.push(<any>{
      id: 'iam',
      label: 'IAM',
      style: 'header',
      children: iamChildren
    })
  }

  const settingsChildren = []

  // Portal Phrases
  settingsChildren.push({
    id: 'settings-phrases',
    label: 'Portal Phrases',
    onClick() {
      context.openView('/settings/phrases', {})
    }
  })

  if (authorizeAll(['registry/inventories:read', 'registry/inventories:write'])) {
    settingsChildren.push({
      id: 'settings-inventories',
      label: 'Inventories',
      onClick() {
        context.openView('/settings/inventories', {})
      }
    })
  }

  if (authorizeAll(['registry/channels:read', 'registry/channels:write'])) {
    settingsChildren.push({
      id: 'settings-channels',
      label: 'Channels',
      onClick() {
        context.openView('/settings/channels', {})
      }
    })
  }

  if (authorizeAll(['registry/locales:read', 'registry/locales:write'])) {
    settingsChildren.push({
      id: 'settings-locales',
      label: 'Locales',
      onClick() {
        context.openView('/settings/locales', {})
      }
    })
  }

  if (authorizeAll(['registry/currencies:read', 'registry/currencies:write'])) {
    settingsChildren.push({
      id: 'settings-currencies',
      label: 'Currencies',
      onClick() {
        context.openView('/settings/currencies', {})
      }
    })
  }

  if (settingsChildren.length > 0) {
    systemChildren.push({
      id: 'settings',
      label: 'Settings',
      children: settingsChildren
    })
  }

  nodes.value.push(<any>{
    id: 'system',
    label: 'System',
    style: 'header',
    children: systemChildren
  })

  const appsSections = apps.getNavHubSections()
  let idx = 0
  for (const section of appsSections) {

    let parent = nodes.value.find(r => r.id === section.id)
    let newParent = false
    if (!parent) {
      newParent = true
      parent = {
        id: section.id,
        label: phrases(capitalize(section.id)),
        children: [],
        style: 'header' as UnwrapRef<TreeNode["style"]>,
      }
    }

    if (parent && !parent.children) {
      parent.children = []
    }

    for (const item of section.items) {

      if (!parent || !parent.children || !authorizeAll(item.accessPatterns)) {
        continue;
      }

      parent.children!.push({
        id: `custom_${++idx}`,
        label: item.label,
        onClick: () => {
          item.action({
            openView(uri: string, options: ViewOptions): Readonly<ViewContext> {
              const view = context.openView(uri, {})
              view.name.value = item.label
              return view
            }
          })
        }
      })
    }

    if (newParent && parent && parent.children && parent.children.length > 0) {
      // Insert before system menu
      nodes.value.splice(nodes.value.length - 1, 0, parent)
    }
  }
}

function capitalize(input: string) {
  input = input.replace(/((?<![\.\s])[A-Z])/g, ' $1');
  input = input.replace(/(\.\w)/g, (match) => '.' + match.charAt(1).toUpperCase());
  input = input.charAt(0).toUpperCase() + input.slice(1);
  return input;
}

function queryFolderToTreeNode(folder: QueryFolder, isShared = false): TreeNode {

  if (folder.id === sharedFolderId) {
    isShared = true;
  }

  const children = folder.children.map(f => queryFolderToTreeNode(f, isShared)) as TreeNode[];

  const childQueries = Object.values(queries.queries).filter(r => r.folderId === folder.id) || [];

  children.push(...childQueries.map(q => ({
    id: q.id,
    label: q.name,
    type: 'query',
    draggable: {
      type: 'query',
      data: {
        queryId: q.id,
      }
    },
    contextMenu: [
      [{
        label: t('workspace.deleteQuery'),
        async action() {

          if (await dialogs.confirm(
            'Are you sure you want to delete this query?' + (isShared ? ' This will delete the shared query for all users.' : ''),
            'Delete query',
            'Delete',
            'Cancel',
            true
          )) {
            queries.deleteQuery(q);
          }
        }
      }, {
        label: t('workspace.copyQueryDef'),
        async action() {

          await navigator.clipboard.writeText(JSON.stringify({
            _hanteraItem: 'query',
            name: q.name,
            navigation: q.navigation,
            columns: q.columns
          }))
        }
      }, {
        label: t('workspace.rename'),
        async action() {
          tree.value?.rename(q.id!);
        },
      }]
    ],
    onClick() {
      context.openView('/query', {
        query: {
          id: q.id
        },
        state: {
          showEditor: false,
          selections: {
            filters: {},
          }
        }
      })
    },
    async onRename(newName) {
      return await queries.renameQuery(q, newName);
    },
  } as TreeNode)));

  const contextMenuNew = [{
    label: t('workspace.newQuery'),
    action() {
      newQueryTab(
        folder.id
      );
    }
  }, {
    label: t('workspace.newFolder'),
    async action() {
      const newFolder = await queries.createFolder(folder, 'New folder');

      if (newFolder === undefined) {
        return;
      }

      nextTick(() => {
        tree.value?.rename(newFolder.id);
      });
    }
  }, buildPasteQueryDefOption(folder.id)] as ContextMenuSection;

  const contextMenuEdit = [] as ContextMenuSection;

  if (folder.id !== sharedFolderId && folder.id !== personalFolderId) {
    contextMenuEdit.push({
      label: t('workspace.rename'),
      async action() {
        tree.value?.rename(folder.id);
      }
    });
    contextMenuEdit.push({
      label: t('workspace.delete'),
      async action() {

        // Check if folder is empty
        if (folder.children.length > 0 || childQueries.length > 0) {
          await dialogs.alert('Folder is not empty. Please delete all queries and subfolders first.', 'Folder not empty');
          return;
        }

        if (await dialogs.confirm(
          'Are you sure you want to delete this folder?' + (isShared ? ' This will delete the shared folder for all users.' : ''),
          'Delete folder',
          'Delete',
          'Cancel',
          true
        )) {
          await queries.deleteFolder(folder.id);
        }
      }
    });
  }

  let name = folder.name;
  if (folder.id === personalFolderId) {
    name = t('workspace.personalQueries');
  } else if (folder.id === sharedFolderId) {
    name = t('workspace.sharedQueries');
  }

  contextMenuNew.key = 'folderNew';
  contextMenuEdit.key = 'folderEdit';

  const contextMenu = [contextMenuNew];
  if (contextMenuEdit.length > 0) {
    contextMenu.push(contextMenuEdit);
  }

  return {
    id: folder.id,
    label: name + (childQueries.length > 0 ? ` (${childQueries.length})` : ''),
    children,
    type: 'queryFolder',
    draggable: {
      type: 'queryFolder',
      data: {
        queryFolderId: folder.id,
      }
    },
    dropZone: {
      accepts: ['query', 'queryFolder'],
      onDrop(payload) {
        if (payload.type === 'query') {
          queries.moveQuery(payload.data.queryId, folder.id)
        } else if (payload.type === 'queryFolder') {
          queries.moveFolder(payload.data.queryFolderId, folder.id)
        }
      },
    },
    async onRename(newName, context) {
      return await queries.renameFolder(context.node.id, newName);
    },
    contextMenu
  } as TreeNode;

  function buildPasteQueryDefOption(folderId: string) {
    return {
      label: t('workspace.pasteQueryDef'),
      async action() {

        const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
        const permissionStatus = await navigator.permissions.query(queryOpts as any);

        if (permissionStatus.state === 'denied') {
          await dialogs.alert('Hantera has been denied access to read from clipboard, please check your browser settings to use this feature.')
          return
        }

        try {
          const clipboardContent = await navigator.clipboard.readText()
          const def = JSON.parse(clipboardContent)
          log.debug('Clipboard content:', def)

          if (def._hanteraItem === 'query') {

            const newQuery: Query = {
              name: getNextFreeName(def.name, 0),
              navigation: def.navigation as QueryNavigation,
              columns: def.columns as QueryColumn[],
              folderId
            }

            await queries.saveQuery(newQuery);
            updateNodes()

            function getNextFreeName(base: string, num: number) {
              let result = base
              if (num > 0) {
                result += ` (${num})`
              }

              const existing = childQueries.find(r => r.name === result)
              if (existing) {
                // Check if existing query ends with (<number>)
                const match = existing.name.match(/\((\d+)\)$/)
                if (match) {
                  const n = parseInt(match[1])
                  return getNextFreeName(base, n + 1)
                } else {
                  return getNextFreeName(base, 2)
                }
              } else {
                return result
              }
            }

          } else {
            await displayError()
          }

        } catch {
          await displayError()
        }

        async function displayError() {

          const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
          const permissionStatus = await navigator.permissions.query(queryOpts as any);

          if (permissionStatus.state === 'denied') {
            await dialogs.alert('Hantera has been denied access to read from clipboard, please check your browser settings to use this feature.')
            return
          }

          await dialogs.alert('No query definition found in clipboard. Did you forget to copy first?')
        }
      }
    }
  }
}

function newQueryTab(folderId: string) {

  context.openView('/query', {
    query: {
      id: 'new',
      folderId: folderId,
    },
    state: {
      showEditor: true,
      transientQuery: {
        name: t('workspace.newQuery'),
        folderId,
        columns: [],
        navigation: {}
      },

      selections: {
        filters: {},
      }
    }
  })
}

</script>

<template>
  <div class="nav-hub">
    <div class="search">
      <QuickSearch />
    </div>

    <div class="body neat-scroll">
      <Tree
        cache-key="workspace-sidebar"
        :nodes="nodes"
        ref="tree"
      />
    </div>

<!--    <ProfileBar />-->
  </div>
</template>

<style scoped>
.nav-hub {
  background-color: hsl(var(--color-90));
  border-right: 1px solid hsl(var(--color-60));
  height: 100%;
  position: relative;

  display: grid;
  grid-template-rows: min-content min-content auto min-content;
  transition: max-width 200ms;
  overflow: hidden;
}

.sidebar--resizing {
  overflow: visible;
}

.sidebar__search {
  padding: 0 5px 5px 5px;
}

.sidebar__body {
  overflow-x: auto;
  display: flex;
  flex-direction: column;
}

.sidebar__separator {
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background-color: hsla(var(--color-60), 0);
  transition: background-color 200ms;
  z-index: 100;
}

.sidebar__separator--dragging {
  background-color: hsla(var(--color-60), 1);
}

.sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  height: 35px;
}

.branding-logo {
  height: 35px;
  padding-left: 5px;
  width: calc(100% - 40px);
  overflow: hidden;
  display: flex;
  align-items: center;
}

.branding-logo svg {
  width: 100px;
  height: 20px;
}

.sidebar__hide-button {
  height: 22px;
  width: 22px;
  padding: 0;
  margin-right: 5px;
  --btn-border-radius: 4px;
}

.sidebar__hide-button svg {
  width: 12px;
  transform: rotate(90deg);
}

@media (max-width: 767px) {
  .sidebar__separator {
    display: none;
  }
}
</style>
